import {
	Alert,
	AsyncSelect,
	Badge,
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	Spinner,
	Stack,
	Table,
	Text,
	Time,
	Tabs,
	TabsList,
	Container,
	Dropdown,
	Component,
	useCallbackRef,
} from "@sembark-travel/ui-base"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import {
	ButtonLink,
	Link,
	NavLink,
	queryToSearch,
} from "@sembark-travel/ui-router"
import {
	CheckboxInputField,
	FieldArray,
	Form,
	GetFieldValue,
	MarkdownInputField,
	SelectField,
	SubmissionError,
	isTruthy,
	TextAreaInputField,
	SelectInputField,
	SwitchInputField,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
	FormApi,
	arrayMutators,
	FileInputField,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { generatePath } from "../router-utils"
import { IListResponse, useXHR } from "@sembark-travel/xhr"
import { ReactNode, useCallback, useEffect, useRef, useState } from "react"
import * as Validator from "yup"
import { Optional } from "utility-types"
import { PlatformIcon } from "./PlatformIcon"
import {
	TIntegratedMailbox,
	TMailIntegratedApp,
	TMailIntegration,
	TNewIntegrationSchema,
	useFetchIntegration,
	useFetchMailIntegratedApp,
} from "./store"
import { SelectTeams, TTeam } from "../Teams"
import {
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import useSWR from "swr"
import {
	Dialog,
	useCreateConfirmationDialogContext,
} from "@sembark-travel/ui-dialog"
import { Markdown } from "@sembark-travel/ui-markdown"
import { MailMessageBody } from "../MailMessages/MailMessageBody"
import { getMailMessagePlainText } from "../shared/mailMessagePlainText"
import config from "../config"

const MAILBOX_SYNC_POLL_INTERVAL = 5_000

function shouldPollMailboxIntegration(data?: TMailIntegratedApp | null) {
	return Boolean(
		data?.mailboxes?.some(
			(mailbox) =>
				mailbox.sync_status === "queued" || mailbox.sync_status === "syncing"
		)
	)
}

function MailboxSyncStatusBadge({ mailbox }: { mailbox: TIntegratedMailbox }) {
	if (mailbox.sync_status === "syncing") {
		return <Badge warning>Syncing</Badge>
	}

	if (mailbox.sync_status === "queued") {
		return <Badge>Queued</Badge>
	}

	if (mailbox.sync_status === "failed") {
		return <Badge status="danger">Sync Failed</Badge>
	}

	if (mailbox.last_synced_at || mailbox.sync_status === "success") {
		return <Badge success>Synced</Badge>
	}

	return <Badge>Not Synced</Badge>
}

function MailboxSyncSummary({ mailbox }: { mailbox: TIntegratedMailbox }) {
	if (mailbox.sync_status === "syncing" && mailbox.last_sync_started_at) {
		return (
			<Stack gap="1">
				<Text color="muted" fontSize="sm">
					Started <RelativeTime timestamp={mailbox.last_sync_started_at} />
				</Text>
			</Stack>
		)
	}

	if (mailbox.sync_status === "queued" && mailbox.last_sync_queued_at) {
		return (
			<Stack gap="1">
				<Text color="muted" fontSize="sm">
					Queued <RelativeTime timestamp={mailbox.last_sync_queued_at} />
				</Text>
			</Stack>
		)
	}

	if (mailbox.last_synced_at) {
		return (
			<Stack gap="1">
				<Text color="muted" fontSize="sm">
					Last synced <RelativeTime timestamp={mailbox.last_synced_at} />
				</Text>
			</Stack>
		)
	}

	return null
}

function MailboxPermissionBadge({
	label,
	value,
}: {
	label: string
	value?: boolean | null
}) {
	if (value === true) {
		return <Badge success>{label}: Allow</Badge>
	}

	if (value === false) {
		return <Badge status="danger">{label}: Deny</Badge>
	}

	return <Badge>{label}: Default</Badge>
}

function MailboxAccessList({ mailbox }: { mailbox: TIntegratedMailbox }) {
	const accessTeams = mailbox.access_teams || []

	if (!mailbox.allow_all_to_read && !accessTeams.length) {
		return null
	}

	return (
		<Stack gap="2">
			<Inline gap="2" alignItems="center">
				<Text color="muted" fontSize="sm" fontWeight="semibold">
					Access
				</Text>
				{mailbox.allow_all_to_read ? <Badge>Everyone can read</Badge> : null}
			</Inline>
			{accessTeams.map((permission) => (
				<Box
					key={permission.team.id}
					padding="2"
					borderWidth="1"
					borderColor="default"
					borderRadius="md"
				>
					<Stack gap="2">
						<Text fontWeight="semibold" fontSize="sm">
							{permission.team.name}
						</Text>
						{permission.team.users?.length ? (
							<Text color="muted" fontSize="sm">
								{permission.team.users
									.map((user) =>
										user.name ? `${user.name} <${user.email}>` : user.email
									)
									.join(", ")}
							</Text>
						) : (
							<Text color="muted" fontSize="sm">
								No users in this team
							</Text>
						)}
						<Inline gap="2" alignItems="center">
							<MailboxPermissionBadge
								label="Read"
								value={permission.can_read}
							/>
							<MailboxPermissionBadge
								label="Templates"
								value={permission.can_send_templates}
							/>
							<MailboxPermissionBadge
								label="Replies"
								value={permission.can_send_replies}
							/>
						</Inline>
					</Stack>
				</Box>
			))}
		</Stack>
	)
}

function MailboxPermissionSelectField({
	label,
	name,
}: {
	label: string
	name: string
}) {
	return (
		<SelectInputField name={name} label={label}>
			<option value="">Default</option>
			<option value="true">Allow</option>
			<option value="false">Deny</option>
		</SelectInputField>
	)
}

function MailboxAccessTeamUsers({ team }: { team?: TTeam | null }) {
	const xhr = useXHR()
	const { data } = useSWR<TTeam>(
		team?.id ? `/api/teams/${team.id}` : null,
		() =>
			xhr.get(`/teams/${team?.id}`).then((resp: { data: { data: TTeam } }) => {
				return resp.data.data
			})
	)

	if (!team?.id) {
		return null
	}

	if (!data) {
		return (
			<Text color="muted" fontSize="sm">
				Loading team users...
			</Text>
		)
	}

	if (!data.users?.length) {
		return (
			<Text color="muted" fontSize="sm">
				No users in this team
			</Text>
		)
	}

	return (
		<Text color="muted" fontSize="sm">
			{data.users
				.map((user) =>
					user.name ? `${user.name} <${user.email}>` : user.email
				)
				.join(", ")}
		</Text>
	)
}

function MailboxAccessDialog({
	mailbox,
	onSaved,
}: {
	mailbox: TIntegratedMailbox
	onSaved: () => Promise<unknown>
}) {
	const xhr = useXHR()
	const [isOpen, setOpen] = useState(false)
	const { data, error, mutate } = useSWR<TIntegratedMailbox>(
		isOpen ? `/api/mailboxes/${mailbox.id}` : null,
		() =>
			xhr
				.get(`/mailboxes/${mailbox.id}`)
				.then((resp: { data: { data: TIntegratedMailbox } }) => resp.data.data)
	)

	return (
		<>
			<Button size="sm" level="secondary" onClick={() => setOpen(true)}>
				<Icons.Security /> Manage Access
			</Button>
			<Dialog
				open={isOpen}
				onClose={() => setOpen(false)}
				title="Mailbox Access"
			>
				<Dialog.Body>
					{!data ? (
						!error ? (
							<Spinner alignCenter padding="8" />
						) : (
							<Alert status="error">
								{error.message || "Unable to load mailbox access."}
							</Alert>
						)
					) : (
						<Form<TMailboxAccessFormValues>
							key={`${mailbox.id}-${data.updated_at || data.created_at}`}
							initialValues={{
								allow_all_to_read: Boolean(data.allow_all_to_read),
								access_teams: (data.access_teams || []).map((d) => ({
									...d,
									can_read:
										d.can_read === true
											? "true"
											: d.can_read === false
												? "false"
												: null,
									can_send_replies:
										d.can_send_replies === true
											? "true"
											: d.can_send_replies === false
												? "false"
												: null,
									can_send_templates:
										d.can_send_templates === true
											? "true"
											: d.can_send_templates === false
												? "false"
												: null,
								})),
							}}
							onSubmit={withServerErrors(async (values) => {
								await xhr.patch(`/mailboxes/${mailbox.id}/access`, {
									allow_all_to_read: values.allow_all_to_read ? 1 : 0,
									access_teams: (values.access_teams || []).flatMap((team) => {
										if (!team.team?.id) {
											return []
										}

										return [
											{
												team_id: team.team.id,
												can_read:
													team.can_read === "true"
														? 1
														: team.can_read === "false"
															? 0
															: null,
												can_send_templates:
													team.can_send_templates === "true"
														? 1
														: team.can_send_templates === "false"
															? 0
															: null,
												can_send_replies:
													team.can_send_replies === "true"
														? 1
														: team.can_send_replies === "false"
															? 0
															: null,
											},
										]
									}),
								})
								await mutate()
								await onSaved()
								showSnackbar("Mailbox access updated successfully.")
								setOpen(false)
							})}
							subscription={{ submitting: true }}
							mutators={{ ...arrayMutators }}
						>
							{({ handleSubmit, submitting }) => (
								<form noValidate onSubmit={handleSubmit}>
									<Stack gap="4">
										<SwitchInputField
											name="allow_all_to_read"
											label="Allow everyone to read this mailbox"
										/>
										<FieldArray<TMailboxAccessTeam> name="access_teams">
											{({ fields }) => (
												<Stack gap="3">
													{fields.map((name, index) => (
														<Box
															key={name}
															padding="3"
															borderWidth="1"
															borderColor="default"
															borderRadius="md"
														>
															<Stack gap="3">
																<Inline gap="3" alignItems="end">
																	<Box flex="1">
																		<SelectField
																			select={SelectTeams}
																			name={`${name}.team`}
																			label="Team"
																			fetchOnMount
																		/>
																	</Box>
																	<Button
																		type="button"
																		level="tertiary"
																		status="danger"
																		onClick={() => fields.remove(index)}
																	>
																		Remove
																	</Button>
																</Inline>
																<GetFieldValue<TTeam | null | undefined>
																	name={`${name}.team`}
																>
																	{({ value: team }) => (
																		<Stack gap="1">
																			{team?.name ? (
																				<Text fontWeight="semibold">
																					{team.name}
																				</Text>
																			) : null}
																			<MailboxAccessTeamUsers team={team} />
																		</Stack>
																	)}
																</GetFieldValue>
																<Grid gap="3">
																	<Col xs={12} md={4}>
																		<MailboxPermissionSelectField
																			label="Read"
																			name={`${name}.can_read`}
																		/>
																	</Col>
																	<Col xs={12} md={4}>
																		<MailboxPermissionSelectField
																			label="Templates"
																			name={`${name}.can_send_templates`}
																		/>
																	</Col>
																	<Col xs={12} md={4}>
																		<MailboxPermissionSelectField
																			label="Replies"
																			name={`${name}.can_send_replies`}
																		/>
																	</Col>
																</Grid>
															</Stack>
														</Box>
													))}
													<Button
														type="button"
														level="secondary"
														onClick={() =>
															fields.push({
																team: null,
																can_read: null,
																can_send_templates: null,
																can_send_replies: null,
															})
														}
													>
														<Icons.Plus /> Add Team
													</Button>
												</Stack>
											)}
										</FieldArray>
										<Divider marginY="0" />
										<SubmissionError />
										<Inline gap="3">
											<Button disabled={submitting} type="submit">
												{submitting ? "Saving..." : "Save Access"}
											</Button>
											<Button
												type="button"
												level="tertiary"
												onClick={() => setOpen(false)}
											>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</form>
							)}
						</Form>
					)}
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type TNewMailboxIntegrationSchema = Pick<
	TNewIntegrationSchema,
	"name" | "description" | "platform"
>

type TMailboxFormValues = {
	preset:
		| "custom"
		| "google_workspace"
		| "microsoft_365"
		| "godaddy"
		| "hostinger"
		| "zoho"
	provider: "imap_smtp"
	name: string
	email: string
	inbound_enabled: boolean
	outbound_enabled: boolean
	push_read_flag_to_imap: boolean
	imap: {
		host: string
		port: number
		encryption: "ssl" | "tls" | "none"
		username: string
		password: string
	}
	smtp: {
		host: string
		port: number
		encryption: "ssl" | "tls" | "none"
		username: string
		password: string
	}
}

type TMailMessage = {
	id: number
	direction?: "inbound" | "outbound"
	created_by?: {
		id: number
		uid?: string
		name?: string | null
		email?: string | null
	} | null
	message_id?: string | null
	subject?: string | null
	from_name?: string | null
	from_email?: string | null
	sent_at?: string | null
	received_at?: string | null
	preview?: string | null
	body_available?: boolean
	html_body?: string | null
	text_body?: string | null
	latest_html_body?: string | null
	latest_text_body?: string | null
	quoted_html_body?: string | null
	quoted_text_body?: string | null
	attachment_count: number
	threads?: Array<{
		id: number
		mailbox_id: number
		subject?: string | null
		normalized_subject?: string | null
		latest_message_at?: string | null
	}>
	participants?: Array<{
		id: number
		type: string
		name?: string | null
		email: string
	}>
}

type TThreadLatestMessageSummary = {
	id: number
	subject?: string | null
	from_name?: string | null
	from_email?: string | null
	sent_at?: string | null
	received_at?: string | null
	text_body?: string | null
	is_read?: boolean | null
	latest_parsing?: {
		id: number
		status: string
		skip_reason?: string | null
		classification?: string | null
		classification_label?: string | null
		error?: string | null
		completed_at?: string | null
	} | null
}

type TMailThread = {
	id: number
	integrated_mailbox_id: number
	subject?: string | null
	normalized_subject?: string | null
	latest_message_at?: string | null
	messages_count?: number
	latest_message?: (TMailMessage & TThreadLatestMessageSummary) | null
	messages?: TMailMessage[]
	links?: TMailThreadLink[]
	mailbox?: { id: number; name?: string | null; email?: string | null } | null
}

type TMailThreadLink = {
	type: "trip_plan_requests" | "trips" | "hotel_bookings"
	id: number
	name: string
}

type TMailboxAccessTeam = {
	team?: TTeam | null
	can_read?: boolean | null | "true" | "false" | ""
	can_send_templates?: boolean | null | "true" | "false" | ""
	can_send_replies?: boolean | null | "true" | "false" | ""
}

type TMailboxAccessFormValues = {
	allow_all_to_read: boolean
	access_teams?: TMailboxAccessTeam[]
}

type TMailboxSettingsFormValues = Pick<
	TMailboxFormValues,
	| "inbound_enabled"
	| "outbound_enabled"
	| "push_read_flag_to_imap"
	| "imap"
	| "smtp"
>

type TMailboxDeleteFormValues = {
	email: string
}

const newMailboxIntegrationSchemaValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to your integration")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please provide a description for this integration")
		.max(500, "Please use 500 or fewer characters for the description"),
})

const mailboxFormValidator = Validator.object().shape({
	name: Validator.string().required("Mailbox name is required"),
	email: Validator.string()
		.email("Please enter a valid mailbox email")
		.required("Mailbox email is required"),
	imap: Validator.object({
		host: Validator.string().required("IMAP host is required"),
		port: Validator.number().required("IMAP port is required"),
		encryption: Validator.string().required("IMAP encryption is required"),
		username: Validator.string().required("IMAP username is required"),
		password: Validator.string().required("IMAP password is required"),
	}),
	smtp: Validator.object({
		host: Validator.string().required("SMTP host is required"),
		port: Validator.number().required("SMTP port is required"),
		encryption: Validator.string().required("SMTP encryption is required"),
		username: Validator.string().required("SMTP username is required"),
		password: Validator.string().required("SMTP password is required"),
	}),
})

function mailboxUsesOAuthProvider(mailbox: TIntegratedMailbox): boolean {
	// Any provider other than the raw IMAP/SMTP one authenticates via an
	// OAuth-style flow (gmail today; outlook, yahoo, etc. tomorrow) and
	// therefore has no IMAP/SMTP credentials to edit on the settings
	// screen — only the operational toggles.
	return mailbox.provider !== "imap_smtp"
}

function getMailboxSettingsFormValidator(mailbox: TIntegratedMailbox) {
	const usesOAuth = mailboxUsesOAuthProvider(mailbox)

	return Validator.object().shape({
		inbound_enabled: Validator.boolean().required(),
		outbound_enabled: Validator.boolean().required(),
		push_read_flag_to_imap: Validator.boolean(),
		imap: Validator.object().when("inbound_enabled", {
			is: (value: boolean) => Boolean(value) && !usesOAuth,
			then: () =>
				Validator.object({
					host: Validator.string().required("IMAP host is required"),
					port: Validator.number().required("IMAP port is required"),
					encryption: Validator.string().required(
						"IMAP encryption is required"
					),
					username: Validator.string().required("IMAP username is required"),
					password: Validator.string().nullable(),
				}),
			otherwise: () => Validator.mixed().nullable(),
		}),
		smtp: Validator.object().when("outbound_enabled", {
			is: (value: boolean) => Boolean(value) && !usesOAuth,
			then: () =>
				Validator.object({
					host: Validator.string().required("SMTP host is required"),
					port: Validator.number().required("SMTP port is required"),
					encryption: Validator.string().required(
						"SMTP encryption is required"
					),
					username: Validator.string().required("SMTP username is required"),
					password: Validator.string().nullable(),
				}),
			otherwise: () => Validator.mixed().nullable(),
		}),
	})
}

function getMailboxDeleteFormValidator(mailbox: TIntegratedMailbox) {
	return Validator.object().shape({
		email: Validator.string()
			.required("Please enter the mailbox email to confirm deletion")
			.oneOf(
				[mailbox.email],
				`Please type ${mailbox.email} exactly to confirm deletion`
			),
	})
}

const MAILBOX_PRESETS = {
	custom: {
		label: "Custom",
		description: "Manually enter your hosting details.",
	},
	google_workspace: {
		label: "Google Workspace / Gmail",
		description: "Uses Gmail IMAP/SMTP defaults.",
		imap: { host: "imap.gmail.com", port: 993, encryption: "ssl" as const },
		smtp: { host: "smtp.gmail.com", port: 465, encryption: "ssl" as const },
	},
	microsoft_365: {
		label: "Microsoft 365 / Outlook",
		description:
			"Basic-auth IMAP/SMTP — Microsoft has disabled this for most tenants. Prefer the Connect Outlook button instead.",
		imap: {
			host: "outlook.office365.com",
			port: 993,
			encryption: "ssl" as const,
		},
		smtp: {
			host: "smtp.office365.com",
			port: 587,
			encryption: "tls" as const,
		},
	},
	godaddy: {
		label: "GoDaddy",
		description: "Common GoDaddy hosted email settings.",
		imap: {
			host: "imap.secureserver.net",
			port: 993,
			encryption: "ssl" as const,
		},
		smtp: {
			host: "smtpout.secureserver.net",
			port: 465,
			encryption: "ssl" as const,
		},
	},
	hostinger: {
		label: "Hostinger",
		description: "Common Hostinger hosted email settings.",
		imap: {
			host: "imap.hostinger.com",
			port: 993,
			encryption: "ssl" as const,
		},
		smtp: {
			host: "smtp.hostinger.com",
			port: 465,
			encryption: "ssl" as const,
		},
	},
	zoho: {
		label: "Zoho Mail",
		description: "Common Zoho Mail settings.",
		imap: { host: "imap.zoho.com", port: 993, encryption: "ssl" as const },
		smtp: { host: "smtp.zoho.com", port: 465, encryption: "ssl" as const },
	},
} as const

export function NewMailboxIntegrationForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	initialValues?: Optional<TNewMailboxIntegrationSchema>
	onSubmit: (data: TNewMailboxIntegrationSchema) => Promise<unknown>
	onCancel: () => void
}) {
	const [initialValues] = useState<TNewMailboxIntegrationSchema>(() => ({
		name: propInitialValues?.name || "",
		description: propInitialValues?.description || "",
		platform: "email_mailbox" as const,
	}))

	return (
		<Form<TNewMailboxIntegrationSchema>
			initialValues={initialValues}
			validate={validateFormValues(newMailboxIntegrationSchemaValidator)}
			onSubmit={withServerErrors(onSubmit)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={4} position="relative">
								<Stack gap="2" position="sticky" top="0">
									<Inline gap="4">
										<Box>
											<PlatformIcon platform="email_mailbox" size="40" />
										</Box>
										<Heading>Email Mailbox Integration</Heading>
									</Inline>
									<Text>
										Create the integration container first, then add one or more
										mailboxes / email accounts under it to start receiving and
										sending emails.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										name="name"
										label="Integration Name"
										placeholder="e.g., Team Email Operations"
										required
									/>
									<TextAreaInputField
										name="description"
										label="Description"
										placeholder="Describe which mailboxes will be managed here"
										rows={3}
										required
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Creating Integration..." : "Create Integration"}
							</Button>
							<Button
								type="button"
								level="tertiary"
								disabled={submitting}
								onClick={onCancel}
							>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

type TSelectOutboundMailboxProps = Omit<
	React.ComponentProps<typeof AsyncSelect>,
	"fetch" | "multiple"
>

function formatOutboundMailbox(mailbox: TIntegratedMailbox) {
	return {
		...mailbox,
		name: `${mailbox.name} (${mailbox.email})`,
	}
}

export function SelectOutboundMailbox(props: TSelectOutboundMailboxProps) {
	const xhr = useXHR()

	return (
		<AsyncSelect
			fetchOnMount
			cacheKey="/mailboxes/outbound"
			perFetchLimit={25}
			{...props}
			fetch={async (q, { page }) => {
				return await xhr
					.get<IListResponse<TIntegratedMailbox>>("/mailboxes/outbound", {
						params: { q, page, limit: 25 },
					})
					.then((resp) => resp.data)
					.then((response) => response.data.map(formatOutboundMailbox))
			}}
		/>
	)
}

export function MailboxIntegratedApp({
	integration,
	children,
}: {
	integration: TMailIntegration
	children?: ({ integration }: { integration: TMailIntegration }) => ReactNode
}) {
	return (
		<Stack gap="6">
			<Inline
				gap="4"
				alignItems="center"
				justifyContent="between"
				flexWrap="wrap"
			>
				<Inline gap="4" alignItems="center">
					<PlatformIcon platform="email_mailbox" size="32" />
					<Stack gap="1">
						<Heading as="h4">Mailbox Integration</Heading>
						<Text color="muted">
							View and test the currently connected mailboxes for this
							integration.
						</Text>
					</Stack>
				</Inline>
			</Inline>
			<Box borderWidth="1" rounded="md" overflow="hidden">
				<Tabs layout="col">
					<TabsList>
						<NavLink
							to={generatePath("/integrations/:integrationId/mail/mailboxes", {
								integrationId: integration.uid,
							})}
						>
							<Icons.Mail /> Mailboxes
						</NavLink>
						<NavLink
							to={generatePath("/integrations/:integrationId/mail/:folder", {
								integrationId: integration.uid,
								folder: "inbox",
							})}
						>
							<Icons.InboxIn /> Inbox
						</NavLink>
					</TabsList>
					<Box>{children?.({ integration })}</Box>
				</Tabs>
			</Box>
		</Stack>
	)
}

export function MailboxesTab({
	integration,
}: {
	integration: TMailIntegration
}) {
	const xhr = useXHR()
	const { data, error, mutate } = useFetchMailIntegratedApp(
		integration.integrated_app_id,
		{
			refreshInterval(currentData) {
				return shouldPollMailboxIntegration(currentData)
					? MAILBOX_SYNC_POLL_INTERVAL
					: 0
			},
		}
	)

	if (!data) {
		if (!error) {
			return <Spinner alignCenter padding="8" />
		}
		return (
			<Alert status="error">
				{error.message || "Unable to load mailboxes"}
			</Alert>
		)
	}

	return (
		<MailboxesTable
			data={data}
			paused={Boolean(integration.paused_at)}
			onConfigUpdated={async () => {
				await mutate()
			}}
			onAccessUpdated={async () => {
				await mutate()
			}}
			onUpdated={async () => {
				await mutate()
			}}
			onDelete={async (mailbox) => {
				await xhr.delete(`/mailboxes/${mailbox.id}`)
				showSnackbar("Mailbox deleted successfully.")
				await mutate()
			}}
			onTest={async (mailbox) => {
				await xhr.post(`/mailboxes/${mailbox.id}/connection-tests`)
				showSnackbar("Mailbox connection test completed.")
				await mutate()
			}}
			onSendTestEmail={async (mailbox) => {
				await xhr.post(`/mailboxes/${mailbox.id}/test-email`)
				showSnackbar(
					`Test email sent to ${mailbox.email}. Check the mailbox inbox or sent folder.`
				)
			}}
		/>
	)
}

export function AddMailboxToIntegration({
	integrationId,
	onSuccess,
	onCancel,
}: {
	integrationId: string | number
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const { data: integration, error } = useFetchIntegration(integrationId)

	if (!integration) {
		if (!error) {
			return <Spinner alignCenter padding="8" />
		}

		return (
			<Alert status="error">
				{error.message || "Unable to load integration details"}
			</Alert>
		)
	}

	if (
		integration.integrated_app_type !== "mail_integrated_apps" ||
		!integration.integrated_app_id
	) {
		return (
			<Alert status="error">
				This integration is not ready for mailbox setup yet.
			</Alert>
		)
	}

	return (
		<AddMailboxForm
			onSubmit={async (values) => {
				const { preset, ...rest } = values
				await xhr.post(
					`/integrations/mail/${integration.integrated_app_id}/mailboxes`,
					{
						...rest,
						inbound_enabled: isTruthy(values.inbound_enabled) ? 1 : 0,
						outbound_enabled: isTruthy(values.outbound_enabled) ? 1 : 0,
						push_read_flag_to_imap: isTruthy(values.push_read_flag_to_imap)
							? 1
							: 0,
					}
				)
				showSnackbar("Mailbox added successfully.")
				onSuccess()
			}}
			onCancel={onCancel}
		/>
	)
}

function AddMailboxForm({
	onSubmit,
	onCancel,
}: {
	onSubmit: (data: TMailboxFormValues) => Promise<unknown>
	onCancel?: () => void
}) {
	const {
		confirm,
		isConfirmationVisible: isTnCConfirmationVisible,
		reject: rejectTnC,
		accept: acceptTnC,
	} = useCreateConfirmationDialogContext()
	const {
		hasScrolledToEnd,
		progress,
		ref: tncContainerRef,
	} = useScrollableContainer()
	const [initialValues] = useState<TMailboxFormValues>(() => ({
		preset: "custom",
		provider: "imap_smtp",
		name: "",
		email: "",
		inbound_enabled: true,
		outbound_enabled: true,
		push_read_flag_to_imap: config.isProduction,
		imap: {
			host: "",
			port: 993,
			encryption: "ssl",
			username: "",
			password: "",
		},
		smtp: {
			host: "",
			port: 465,
			encryption: "ssl",
			username: "",
			password: "",
		},
	}))

	return (
		<>
			<Form<TMailboxFormValues>
				initialValues={initialValues}
				validate={validateFormValues(mailboxFormValidator)}
				onSubmit={withServerErrors(async (values) => {
					if (!(await confirm())) {
						throw new Error("Please accept the terms to continue")
					}
					await onSubmit(values)
				})}
				subscription={{ submitting: true }}
			>
				{({ submitting, handleSubmit, form }) => (
					<form noValidate onSubmit={handleSubmit}>
						<MailboxPresetWatcher form={form} />
						<Stack gap="4">
							<Grid gap="4">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading as="h3">
											<Icons.Security size="6" /> Mailbox Identity
										</Heading>
										<Text color="muted">
											Name the mailbox clearly so your team can distinguish a
											shared inbox from a brand or salesperson mailbox.
										</Text>
									</Stack>
								</Col>
								<Col>
									<Grid gap="4">
										<Col xs={12} md={6}>
											<TextInputField
												name="name"
												label="Mailbox `From` Name"
												placeholder="e.g., Shared Sales Inbox"
												required
												help="This will be used as `Sender Name` for outgoing Emails"
											/>
										</Col>
										<Col xs={12} md={6}>
											<TextInputField
												name="email"
												label="Mailbox Email"
												placeholder="sales@example.com"
												required
											/>
										</Col>
									</Grid>
								</Col>
							</Grid>
							<Divider sm />
							<Grid gap="4">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading as="h3">
											<Icons.Cog size="6" /> Setup Style
										</Heading>
										<Text color="muted">
											Start with a provider preset to fill common values, then
											choose whether this mailbox should handle incoming sync,
											outgoing sending, or both.
										</Text>
									</Stack>
								</Col>
								<Col>
									<Stack gap="4">
										<Grid gap="4">
											<Col xs={12} md={6}>
												<SelectInputField
													name="preset"
													label="Mailbox Preset"
													required
												>
													{Object.entries(MAILBOX_PRESETS).map(
														([value, preset]) => (
															<option key={value} value={value}>
																{preset.label}
															</option>
														)
													)}
												</SelectInputField>
											</Col>
											<Col xs={12} md={6}>
												<SelectInputField
													name="provider"
													label="Provider"
													required
												>
													<option value="imap_smtp">IMAP + SMTP</option>
												</SelectInputField>
											</Col>
										</Grid>
										<Grid gap="4">
											<Col xs={12} md={6}>
												<SwitchInputField
													name="inbound_enabled"
													label="Enable Incoming Sync"
												/>
											</Col>
											<Col xs={12} md={6}>
												<SwitchInputField
													name="outbound_enabled"
													label="Enable Outgoing Send"
												/>
											</Col>
											<Col xs={12}>
												<SwitchInputField
													name="push_read_flag_to_imap"
													label="Push read/unread state back to IMAP"
													help="When on, marking a mail as read here also marks it read on the upstream IMAP server."
												/>
											</Col>
										</Grid>
									</Stack>
								</Col>
							</Grid>
							<GetFieldValue<TMailboxFormValues["preset"]> name="preset">
								{({ value }) => (
									<Alert status="info">
										{MAILBOX_PRESETS[value || "custom"]?.description}
										{value !== "custom"
											? " You can still change any field below if your mailbox uses different values."
											: null}
									</Alert>
								)}
							</GetFieldValue>
							<GetFieldValue<
								TMailboxFormValues["inbound_enabled"]
							> name="inbound_enabled">
								{({ value }) =>
									isTruthy(value) ? (
										<>
											<Divider sm />
											<Grid gap="4">
												<Col sm={12} md={4}>
													<Stack gap="1">
														<Heading as="h3">
															<Icons.InboxIn size="6" /> Incoming Server
														</Heading>
														<Text color="muted">
															Set the IMAP details used to read incoming mail
															and sync conversations from this mailbox.
														</Text>
													</Stack>
												</Col>
												<Col>
													<Grid gap="4">
														<Col xs={12} md={6}>
															<TextInputField
																name="imap.host"
																label="IMAP Host"
																required
															/>
														</Col>
														<Col xs={12} md={3}>
															<TextInputField
																name="imap.port"
																label="Port"
																type="number"
																required
															/>
														</Col>
														<Col xs={12} md={3}>
															<SelectInputField
																name="imap.encryption"
																label="Encryption"
																required
															>
																<option value="ssl">SSL</option>
																<option value="tls">TLS / STARTTLS</option>
																<option value="none">None</option>
															</SelectInputField>
														</Col>
														<Col xs={12} md={6}>
															<TextInputField
																name="imap.username"
																label="Username"
																required
															/>
														</Col>
														<Col xs={12} md={6}>
															<TextInputField
																name="imap.password"
																label="Password / App Password"
																type="password"
																required
															/>
														</Col>
													</Grid>
												</Col>
											</Grid>
										</>
									) : null
								}
							</GetFieldValue>
							<GetFieldValue<
								TMailboxFormValues["outbound_enabled"]
							> name="outbound_enabled">
								{({ value }) =>
									isTruthy(value) ? (
										<>
											<Divider sm />
											<Grid gap="4">
												<Col sm={12} md={4}>
													<Stack gap="1">
														<Heading as="h3">
															<Icons.Mail size="6" /> Outgoing Server
														</Heading>
														<Text color="muted">
															Set the SMTP details used to send replies and
															booking emails from this mailbox identity.
														</Text>
													</Stack>
												</Col>
												<Col>
													<Grid gap="4">
														<Col xs={12} md={6}>
															<TextInputField
																name="smtp.host"
																label="SMTP Host"
																required
															/>
														</Col>
														<Col xs={12} md={3}>
															<TextInputField
																name="smtp.port"
																label="Port"
																type="number"
																required
															/>
														</Col>
														<Col xs={12} md={3}>
															<SelectInputField
																name="smtp.encryption"
																label="Encryption"
																required
															>
																<option value="ssl">SSL</option>
																<option value="tls">TLS / STARTTLS</option>
																<option value="none">None</option>
															</SelectInputField>
														</Col>
														<Col xs={12} md={6}>
															<TextInputField
																name="smtp.username"
																label="Username"
																required
															/>
														</Col>
														<Col xs={12} md={6}>
															<TextInputField
																name="smtp.password"
																label="Password / App Password"
																type="password"
																required
															/>
														</Col>
													</Grid>
												</Col>
											</Grid>
										</>
									) : null
								}
							</GetFieldValue>
							<Divider sm />
							<Grid gap="4">
								<Col sm={12} md={{ offset: 4, span: 8 }}>
									<Stack gap="4" paddingLeft={{ md: "4" }}>
										<SubmissionError />
										<Inline gap="4">
											<Button type="submit" disabled={submitting}>
												{submitting ? "Saving Mailbox..." : "Add Mailbox"}
											</Button>
											{onCancel ? (
												<Button
													type="button"
													level="tertiary"
													disabled={submitting}
													onClick={onCancel}
												>
													Cancel
												</Button>
											) : null}
										</Inline>
									</Stack>
								</Col>
							</Grid>
						</Stack>
					</form>
				)}
			</Form>
			<Dialog open={isTnCConfirmationVisible} onClose={rejectTnC} lg>
				<Dialog.Header>
					<Dialog.Title>
						Terms & Conditions for Mailbox Integration
					</Dialog.Title>
				</Dialog.Header>
				<Dialog.Body>
					<Form
						initialValues={{ check_1: 0, check_2: 0, check_3: 0 }}
						onSubmit={withServerErrors(async (values) => {
							if (
								!isTruthy(values.check_1) ||
								!isTruthy(values.check_2) ||
								!isTruthy(values.check_3)
							) {
								throw new Error(
									"Please accept all the checkbox options to continue."
								)
							}
							acceptTnC()
						})}
					>
						{({ handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									<Text>
										By connecting a mailbox you authorise Sembark Tech Private
										Limited to access, sync and store messages from the
										configured mailbox using the credentials you provide,
										strictly for the purpose of operating your CRM workflow.
										Continued use of the integration constitutes acceptance of
										these terms.
									</Text>
									<Box position="relative" rounded="md" overflow="hidden">
										<Box
											style={{ maxHeight: "400px" }}
											overflow="auto"
											padding="4"
											borderWidth="2"
											rounded="md"
											bgColor="inset"
											ref={tncContainerRef}
										>
											<Markdown>{MAILBOX_TNC}</Markdown>
										</Box>
										<Box
											style={{
												height: "30px",
												opacity: (100 - progress) / 100,
												background:
													"linear-gradient(to bottom, transparent, black)",
											}}
											position="absolute"
											left="0"
											bottom="0"
											right="0"
										></Box>
										<Box
											position="absolute"
											bottom="0"
											left="0"
											width="full"
											style={{
												transformOrigin: "left center",
												transform: `scaleX(${progress / 100})`,
												height: "4px",
											}}
											bgColor="primary_emphasis"
										></Box>
									</Box>
									<Text>
										We may update or amend these Terms & Conditions from time to
										time. Continued use of the integration after a change
										indicates acceptance of the revised terms.
									</Text>
									{!hasScrolledToEnd ? (
										<Alert inline status="error">
											<Text fontWeight="semibold">
												Please read the full terms before continuing.
											</Text>
										</Alert>
									) : null}
									<Stack
										gap="2"
										padding="4"
										borderWidth="1"
										rounded="md"
										opacity={hasScrolledToEnd ? "100" : "70"}
										bgColor={hasScrolledToEnd ? "primary" : "default"}
									>
										<CheckboxInputField
											name="check_1"
											label="I confirm the mailbox credentials I am about to enter belong to my organisation and I am authorised to use them with Sembark."
											disabled={!hasScrolledToEnd}
										/>
										<CheckboxInputField
											name="check_2"
											label="I acknowledge that Sembark Tech Private Limited stores these credentials encrypted at rest and uses them only to sync and send mail on my behalf."
											disabled={!hasScrolledToEnd}
										/>
										<CheckboxInputField
											name="check_3"
											label="I accept that Sembark is a technology provider only; mailbox availability, deliverability and provider-side issues remain my responsibility with the upstream provider."
											disabled={!hasScrolledToEnd}
										/>
									</Stack>
								</Stack>
								<Divider sm />
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={!hasScrolledToEnd}>
											I Agree, Please Proceed.
										</Button>
										<Button onClick={rejectTnC} level="tertiary">
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function MailboxSettingsDialog({
	mailbox,
	onSaved,
	children,
}: {
	mailbox: TIntegratedMailbox
	onSaved: () => Promise<unknown>
	children: (props: { edit: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, setOpen] = useState(false)
	const usesOAuth = mailboxUsesOAuthProvider(mailbox)
	const initialValues: TMailboxSettingsFormValues = {
		inbound_enabled: mailbox.inbound_enabled,
		outbound_enabled: mailbox.outbound_enabled,
		push_read_flag_to_imap:
			typeof mailbox.push_read_flag_to_imap === "boolean"
				? mailbox.push_read_flag_to_imap
				: config.isProduction,
		imap: {
			host: mailbox.credentials?.imap?.host || "",
			port: mailbox.credentials?.imap?.port || 993,
			encryption: mailbox.credentials?.imap?.encryption || "ssl",
			username: mailbox.credentials?.imap?.username || "",
			password: "",
		},
		smtp: {
			host: mailbox.credentials?.smtp?.host || "",
			port: mailbox.credentials?.smtp?.port || 465,
			encryption: mailbox.credentials?.smtp?.encryption || "ssl",
			username: mailbox.credentials?.smtp?.username || "",
			password: "",
		},
	}

	return (
		<>
			{children({ edit: () => setOpen(true) })}
			<Dialog
				open={isOpen}
				onClose={() => setOpen(false)}
				title={`Edit Mailbox "${mailbox.name}" <${mailbox.email}>`}
				lg
			>
				<Dialog.Body>
					<Form<TMailboxSettingsFormValues>
						initialValues={initialValues}
						validate={validateFormValues(
							getMailboxSettingsFormValidator(mailbox)
						)}
						onSubmit={withServerErrors(async (values) => {
							await xhr.patch(`/mailboxes/${mailbox.id}`, {
								inbound_enabled: values.inbound_enabled ? 1 : 0,
								outbound_enabled: values.outbound_enabled ? 1 : 0,
								push_read_flag_to_imap: values.push_read_flag_to_imap ? 1 : 0,
								imap:
									!usesOAuth && values.inbound_enabled
										? values.imap
										: undefined,
								smtp:
									!usesOAuth && values.outbound_enabled
										? values.smtp
										: undefined,
							})
							await onSaved()
							showSnackbar("Mailbox settings updated successfully.")
							setOpen(false)
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									<Alert status="info">
										{usesOAuth
											? "Authentication is handled by the provider via OAuth. Toggle inbound sync, outbound send, and read-flag mirroring below; the app will only sync or send when the matching switch is on, regardless of what scopes the provider has granted."
											: "Update the sync/send settings here. Leave a password blank to keep the existing one."}
									</Alert>
									<Grid gap="4">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading as="h3">
													<Icons.Cog size="6" /> Setup Style
												</Heading>
												<Text color="muted">
													Choose whether this mailbox should handle incoming
													sync, outgoing sending, or both.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Grid gap="4">
												<Col xs={12} md={6}>
													<SwitchInputField
														name="inbound_enabled"
														label="Enable Incoming Sync"
													/>
												</Col>
												<Col xs={12} md={6}>
													<SwitchInputField
														name="outbound_enabled"
														label="Enable Outgoing Send"
													/>
												</Col>
												<Col xs={12}>
													<SwitchInputField
														name="push_read_flag_to_imap"
														label="Push read/unread state back to IMAP"
														help="When on, marking a mail as read here also marks it read on the upstream IMAP server."
													/>
												</Col>
											</Grid>
										</Col>
									</Grid>
									<GetFieldValue<boolean> name="inbound_enabled">
										{({ value: inboundEnabled }) =>
											inboundEnabled && !usesOAuth ? (
												<>
													<Divider sm />
													<Grid gap="4">
														<Col sm={12} md={4}>
															<Stack gap="1">
																<Heading as="h3">
																	<Icons.InboxIn size="6" /> Incoming Server
																</Heading>
																<Text color="muted">
																	Set the IMAP details used to read incoming
																	mail and sync conversations from this mailbox.
																</Text>
															</Stack>
														</Col>
														<Col>
															<Grid gap="4">
																<Col xs={12} md={6}>
																	<TextInputField
																		name="imap.host"
																		label="IMAP Host"
																		required
																	/>
																</Col>
																<Col xs={12} md={3}>
																	<TextInputField
																		name="imap.port"
																		label="Port"
																		type="number"
																		required
																	/>
																</Col>
																<Col xs={12} md={3}>
																	<SelectInputField
																		name="imap.encryption"
																		label="Encryption"
																		required
																	>
																		<option value="ssl">SSL</option>
																		<option value="tls">TLS / STARTTLS</option>
																		<option value="none">None</option>
																	</SelectInputField>
																</Col>
																<Col xs={12} md={6}>
																	<TextInputField
																		name="imap.username"
																		label="Username"
																		required
																	/>
																</Col>
																<Col xs={12} md={6}>
																	<TextInputField
																		name="imap.password"
																		label="Password / App Password"
																		type="password"
																		secondaryLabel="optional"
																	/>
																</Col>
															</Grid>
														</Col>
													</Grid>
												</>
											) : null
										}
									</GetFieldValue>
									<GetFieldValue<boolean> name="outbound_enabled">
										{({ value: outboundEnabled }) =>
											outboundEnabled && !usesOAuth ? (
												<>
													<Divider sm />
													<Grid gap="4">
														<Col sm={12} md={4}>
															<Stack gap="1">
																<Heading as="h3">
																	<Icons.Mail size="6" /> Outgoing Server
																</Heading>
																<Text color="muted">
																	Set the SMTP details used to send replies and
																	booking emails from this mailbox identity.
																</Text>
															</Stack>
														</Col>
														<Col>
															<Grid gap="4">
																<Col xs={12} md={6}>
																	<TextInputField
																		name="smtp.host"
																		label="SMTP Host"
																		required
																	/>
																</Col>
																<Col xs={12} md={3}>
																	<TextInputField
																		name="smtp.port"
																		label="Port"
																		type="number"
																		required
																	/>
																</Col>
																<Col xs={12} md={3}>
																	<SelectInputField
																		name="smtp.encryption"
																		label="Encryption"
																		required
																	>
																		<option value="ssl">SSL</option>
																		<option value="tls">TLS / STARTTLS</option>
																		<option value="none">None</option>
																	</SelectInputField>
																</Col>
																<Col xs={12} md={6}>
																	<TextInputField
																		name="smtp.username"
																		label="Username"
																		required
																	/>
																</Col>
																<Col xs={12} md={6}>
																	<TextInputField
																		name="smtp.password"
																		label="Password / App Password"
																		type="password"
																		secondaryLabel="optional"
																	/>
																</Col>
															</Grid>
														</Col>
													</Grid>
												</>
											) : null
										}
									</GetFieldValue>
									<Divider marginY="0" />
									<Grid gap="0">
										<Col sm={12} md={{ offset: 4, span: 8 }}>
											<Stack gap="4" paddingLeft={{ md: "4" }}>
												<SubmissionError />
												<Inline gap="4">
													<Button type="submit" disabled={submitting}>
														{submitting ? "Saving..." : "Save Settings"}
													</Button>
													<Button
														type="button"
														level="tertiary"
														onClick={() => setOpen(false)}
														disabled={submitting}
													>
														Cancel
													</Button>
												</Inline>
											</Stack>
										</Col>
									</Grid>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function MailboxActiveStatusDialog({
	mailbox,
	onSaved,
	children,
}: {
	mailbox: TIntegratedMailbox
	onSaved: () => Promise<unknown>
	children: (props: { togglePause: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, setOpen] = useState(false)
	const nextState = !mailbox.is_active

	return (
		<>
			{children({ togglePause: () => setOpen(true) })}
			<Dialog
				open={isOpen}
				onClose={() => setOpen(false)}
				title={
					mailbox.is_active
						? `Pause Mailbox "${mailbox.name}"`
						: `Activate Mailbox "${mailbox.name}"`
				}
				sm
			>
				<Dialog.Body>
					<Form<Record<string, never>>
						initialValues={{}}
						onSubmit={withServerErrors(async () => {
							await xhr.patch(`/mailboxes/${mailbox.id}/active`, {
								is_active: nextState ? 1 : 0,
							})
							showSnackbar(nextState ? "Mailbox activated." : "Mailbox paused.")
							await onSaved()
							setOpen(false)
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									{mailbox.is_active ? (
										<Alert status="warning">
											Pausing this mailbox stops inbox sync and prevents
											sending. Existing threads and messages remain available.
										</Alert>
									) : (
										<Alert status="info">
											Activating this mailbox resumes inbox sync and outbound
											sending.
										</Alert>
									)}
									<SubmissionError />
									<Inline gap="3">
										<Button type="submit" disabled={submitting}>
											{submitting
												? "Saving..."
												: mailbox.is_active
													? "Pause Mailbox"
													: "Activate Mailbox"}
										</Button>
										<Button
											type="button"
											level="tertiary"
											onClick={() => setOpen(false)}
											disabled={submitting}
										>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function MailboxDeleteDialog({
	mailbox,
	onDelete,
	children,
}: {
	mailbox: TIntegratedMailbox
	onDelete: (mailbox: TIntegratedMailbox) => Promise<unknown>
	children: (props: { confirmDelete: () => void }) => React.ReactNode
}) {
	const [isOpen, setOpen] = useState(false)

	return (
		<>
			{children({ confirmDelete: () => setOpen(true) })}
			<Dialog
				open={isOpen}
				onClose={() => setOpen(false)}
				title={`Delete Mailbox "${mailbox.name}"`}
				sm
			>
				<Dialog.Body>
					<Form<TMailboxDeleteFormValues>
						initialValues={{ email: "" }}
						validate={validateFormValues(
							getMailboxDeleteFormValidator(mailbox)
						)}
						onSubmit={withServerErrors(async () => {
							await onDelete(mailbox)
							setOpen(false)
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									<Alert status="error">
										This action is irreversible. Deleting this mailbox will
										remove the mailbox and all synced messages from the system.
									</Alert>
									<Text color="muted">
										You can re-add the mailbox later if required, but the
										synchronisation process will start from zero.
									</Text>
									<TextInputField
										name="email"
										label="Type the mailbox email to confirm"
										placeholder={mailbox.email}
										required
									/>
									<Divider marginY={"0"} />
									<SubmissionError />
									<Inline gap="3">
										<Button type="submit" status="danger" disabled={submitting}>
											{submitting ? "Deleting..." : "Delete Mailbox"}
										</Button>
										<Button
											type="button"
											level="tertiary"
											onClick={() => setOpen(false)}
											disabled={submitting}
										>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function MailboxPresetWatcher({ form }: { form: FormApi<TMailboxFormValues> }) {
	const { value: preset } =
		useFieldValue<TMailboxFormValues["preset"]>("preset")
	const previousPreset = useRef<TMailboxFormValues["preset"] | undefined>(
		undefined
	)

	useEffect(() => {
		if (!preset || preset === previousPreset.current) {
			return
		}

		previousPreset.current = preset

		if (preset === "custom") {
			return
		}

		const values = form.getState().values
		const presetConfig = MAILBOX_PRESETS[preset]
		const username =
			values.email || values.imap.username || values.smtp.username

		form.batch(() => {
			form.change("provider", "imap_smtp")
			form.change("imap.host" as never, presetConfig.imap.host as never)
			form.change("imap.port" as never, presetConfig.imap.port as never)
			form.change(
				"imap.encryption" as never,
				presetConfig.imap.encryption as never
			)
			form.change("smtp.host" as never, presetConfig.smtp.host as never)
			form.change("smtp.port" as never, presetConfig.smtp.port as never)
			form.change(
				"smtp.encryption" as never,
				presetConfig.smtp.encryption as never
			)
			if (username) {
				form.change("imap.username" as never, username as never)
				form.change("smtp.username" as never, username as never)
			}
		})
	}, [form, preset])

	return null
}

function MailboxRowActions({
	mailbox,
	oauthAuthUrls,
	onUpdated,
	onDelete,
	onTest,
	onSendTestEmail,
	onAccessUpdated,
}: {
	mailbox: TIntegratedMailbox
	// Fresh connect/reconnect entry points per OAuth provider; null when
	// that provider has no configured OAuth client.
	oauthAuthUrls: { gmail: string | null; outlook: string | null }
	onUpdated: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onDelete: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onTest: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onSendTestEmail: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onAccessUpdated: (mailbox: TIntegratedMailbox) => Promise<unknown>
}) {
	const xhr = useXHR()
	const [testing, setTesting] = useState(false)
	const [sending, setSending] = useState(false)
	const [syncing, setSyncing] = useState(false)

	const runSync = async () => {
		if (syncing) return
		if (mailbox.sync_status === "queued" || mailbox.sync_status === "syncing") {
			alert("Synchronisation is in-process. Please retry after sometime.")
			return
		}
		setSyncing(true)
		try {
			await xhr.post(`/mailboxes/${mailbox.id}/sync`)
			showSnackbar("Mailbox inbox sync queued.")
			await onUpdated(mailbox)
		} catch (e) {
			const error = e as Error
			alert(error.message || "Something went wrong. Please try again.")
		} finally {
			setSyncing(false)
		}
	}

	const runTest = async () => {
		if (testing) return
		setTesting(true)
		try {
			await onTest(mailbox)
		} catch (e) {
			const error = e as Error
			alert(error.message || "Something went wrong. Please try again.")
		} finally {
			setTesting(false)
		}
	}

	const runSendTest = async () => {
		if (sending) return
		setSending(true)
		try {
			await onSendTestEmail(mailbox)
		} catch (e) {
			const error = e as Error
			alert(error.message || "Something went wrong. Please try again.")
		} finally {
			setSending(false)
		}
	}

	return (
		<Inline gap="2" justifyContent="end">
			<MailboxAccessDialog
				mailbox={mailbox}
				onSaved={() => onAccessUpdated(mailbox)}
			/>
			{!mailbox.is_active ? (
				// When the mailbox is already paused, surface Activate as a
				// first-class outside action so resuming the mailbox is not
				// hidden inside the overflow menu.
				<MailboxActiveStatusDialog
					mailbox={mailbox}
					onSaved={() => onUpdated(mailbox)}
				>
					{({ togglePause }) => (
						<Button
							size="sm"
							level="secondary"
							status="warning"
							onClick={togglePause}
						>
							<Icons.Refresh /> Activate
						</Button>
					)}
				</MailboxActiveStatusDialog>
			) : null}
			<Dropdown alignRight>
				<Dropdown.ToggleButton size="sm" level="tertiary">
					<Icons.DotsVertical />
				</Dropdown.ToggleButton>
				<Dropdown.Menu>
					<MailboxSettingsDialog
						mailbox={mailbox}
						onSaved={() => onUpdated(mailbox)}
					>
						{({ edit }) => (
							<Dropdown.MenuItem onClick={edit}>
								<Icons.Cog /> Edit Settings
							</Dropdown.MenuItem>
						)}
					</MailboxSettingsDialog>
					{mailbox.provider !== "imap_smtp" &&
					oauthAuthUrls[mailbox.provider] ? (
						<Dropdown.MenuItem
							as="a"
							href={oauthAuthUrls[mailbox.provider] as string}
							target="_blank"
							rel="noreferrer noopener"
							title="Re-run the OAuth consent to refresh stored access. The backend matches by email and updates this row in place."
						>
							<Icons.SwitchHorizontal /> Reconnect{" "}
							{mailbox.provider === "gmail" ? "Gmail" : "Outlook"}
						</Dropdown.MenuItem>
					) : null}
					<Dropdown.MenuItemDivider />
					{mailbox.is_active && mailbox.inbound_enabled ? (
						<Dropdown.MenuItem
							onClick={(e) => {
								e.preventDefault()
								void runSync()
							}}
						>
							<Icons.Refresh spin={syncing} />{" "}
							{syncing ? "Syncing..." : "Sync Now"}
						</Dropdown.MenuItem>
					) : null}
					<Dropdown.MenuItem
						onClick={(e) => {
							e.preventDefault()
							void runTest()
						}}
					>
						<Icons.TestTubeDiagonal spin={testing} />{" "}
						{testing ? "Testing..." : "Test Connection"}
					</Dropdown.MenuItem>
					{mailbox.outbound_enabled ? (
						<Dropdown.MenuItem
							onClick={(e) => {
								e.preventDefault()
								void runSendTest()
							}}
						>
							<Icons.Mail /> {sending ? "Sending..." : "Send Test Email"}
						</Dropdown.MenuItem>
					) : null}
					<Dropdown.MenuItemDivider />
					{mailbox.is_active ? (
						<MailboxActiveStatusDialog
							mailbox={mailbox}
							onSaved={() => onUpdated(mailbox)}
						>
							{({ togglePause }) => (
								<Dropdown.MenuItem onClick={togglePause}>
									<Icons.Ban /> Pause
								</Dropdown.MenuItem>
							)}
						</MailboxActiveStatusDialog>
					) : (
						<MailboxDeleteDialog mailbox={mailbox} onDelete={onDelete}>
							{({ confirmDelete }) => (
								<Dropdown.MenuItem onClick={confirmDelete} color="danger">
									<Icons.Trash /> Delete
								</Dropdown.MenuItem>
							)}
						</MailboxDeleteDialog>
					)}
				</Dropdown.Menu>
			</Dropdown>
		</Inline>
	)
}

const MAILBOX_PROVIDER_LABELS: Record<TIntegratedMailbox["provider"], string> =
	{
		imap_smtp: "IMAP/SMTP",
		gmail: "Gmail OAuth",
		outlook: "Outlook OAuth",
	}

type TGmailOAuthFormValues = {
	client_id: string
	client_secret: string
}

const gmailOAuthFormValidator = Validator.object().shape({
	client_id: Validator.string().required("Client ID is required"),
	client_secret: Validator.string().required("Client Secret is required"),
})

// Google's downloaded credentials.json nests the client under `web`
// (web app) or `installed` (desktop). Parse client-side only — the file
// itself is never uploaded.
function parseGoogleCredentialsFile(text: string): {
	client_id?: string
	client_secret?: string
} {
	try {
		const json = JSON.parse(text) as Record<string, unknown>
		const creds = (json.web || json.installed || json) as Record<
			string,
			unknown
		>
		return {
			client_id:
				typeof creds.client_id === "string" ? creds.client_id : undefined,
			client_secret:
				typeof creds.client_secret === "string"
					? creds.client_secret
					: undefined,
		}
	} catch {
		return {}
	}
}

function GmailOAuthSettingsDialog({
	app,
	onSaved,
}: {
	app: TMailIntegratedApp
	onSaved: () => Promise<unknown>
}) {
	const xhr = useXHR()
	const [isOpen, setOpen] = useState(false)
	const [clearing, setClearing] = useState(false)
	const configured = Boolean(app.gmail_oauth_configured)
	const callbackUrl = app.gmail_callback_url || ""
	const authUrl = app.gmail_auth_url || ""

	const clearCredentials = async () => {
		if (clearing) return
		if (
			!window.confirm(
				"Remove the custom Gmail OAuth client and revert to the default? Active Gmail mailboxes connected under it must be paused first."
			)
		) {
			return
		}
		setClearing(true)
		try {
			await xhr.delete(`/integrations/mail/${app.id}/gmail-oauth`)
			showSnackbar("Custom Gmail OAuth client removed.")
			await onSaved()
			setOpen(false)
		} catch (e) {
			const error = e as Error
			alert(error.message || "Unable to remove the Gmail OAuth client.")
		} finally {
			setClearing(false)
		}
	}

	return (
		<>
			<Button level="secondary" onClick={() => setOpen(true)}>
				<Icons.Cog /> Gmail OAuth{" "}
				{configured ? (
					<Badge success>Custom</Badge>
				) : authUrl ? (
					<Badge>Default</Badge>
				) : null}
			</Button>
			<Dialog
				open={isOpen}
				onClose={() => setOpen(false)}
				title="Gmail OAuth Client"
				lg
			>
				<Dialog.Body>
					<Form<TGmailOAuthFormValues>
						initialValues={{
							client_id: app.gmail_oauth_client_id || "",
							client_secret: "",
						}}
						validate={validateFormValues(gmailOAuthFormValidator)}
						onSubmit={withServerErrors(async (values) => {
							await xhr.put(`/integrations/mail/${app.id}/gmail-oauth`, {
								client_id: values.client_id,
								client_secret: values.client_secret,
							})
							showSnackbar("Gmail OAuth client saved.")
							await onSaved()
							setOpen(false)
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit, form }) => (
							<form noValidate onSubmit={handleSubmit} autoComplete="off">
								<Stack gap="4">
									<Alert status="info">
										Connect Gmail using your own Google Cloud OAuth client while
										our app awaits verification. In Google Cloud Console create
										an <b>OAuth client ID</b> of type <b>Web application</b>,
										add the redirect URI below, then paste the Client ID and
										Secret — or upload the downloaded{" "}
										<code>credentials.json</code>. Remove the custom client once
										our app is verified to fall back to the default.
									</Alert>

									<Stack gap="2">
										<Heading as="h4">
											Create a Google OAuth client to get the Client ID and
											Secret
										</Heading>
										<Text color="muted" fontSize="sm">
											One-time setup in Google Cloud Console. Use the Google
											account that owns the mailbox you want to connect.
										</Text>
										<Stack
											as="ol"
											listStyleType="decimal"
											paddingLeft="4"
											gap="2"
										>
											<Box as="li">
												Open the{" "}
												<Text
													as="a"
													href="https://console.cloud.google.com/"
													rel="noopener noreferrer"
													target="_blank"
													color="accent"
												>
													Google Cloud Console
												</Text>{" "}
												and sign in.
											</Box>
											<Box as="li">
												Create a project: top-bar project dropdown →{" "}
												<mark>New Project</mark>, name it (e.g.{" "}
												<b>Sembark Mail</b>), click <mark>Create</mark>, then
												select it.
											</Box>
											<Box as="li">
												Enable the API: left menu →{" "}
												<b>APIs &amp; Services → Library</b>, search{" "}
												<b>Gmail API</b>, open it and click <mark>Enable</mark>.
											</Box>
											<Box as="li">
												Configure the consent screen:{" "}
												<b>APIs &amp; Services → OAuth consent screen</b>,
												choose <b>External</b>, fill the App name, user support
												email and developer email, then save. Under{" "}
												<b>Audience → Test users</b> add your own Google address
												— this lets you connect without waiting for Google
												verification.
											</Box>
											<Box as="li">
												Create the client:{" "}
												<b>APIs &amp; Services → Credentials</b> →{" "}
												<mark>Create credentials</mark> → <b>OAuth client ID</b>{" "}
												→ Application type <b>Web application</b>, and give it a
												name.
											</Box>
											<Box as="li">
												<Text>
													Under <b>Authorized redirect URIs</b> click{" "}
													<mark>Add URI</mark> and paste the{" "}
													<b>redirect URI shown below</b>, then click{" "}
													<mark>Create</mark>.
												</Text>
												{callbackUrl ? (
													<CopyToClipboard<HTMLDivElement>>
														{({ nodeToCopy, copyNodeTextToClipboard }) => (
															<Stack gap="1">
																<Text fontWeight="semibold">
																	Authorized redirect URI
																</Text>
																<Inline
																	gap="2"
																	alignItems="center"
																	paddingY="1"
																	paddingLeft="4"
																	paddingRight="1"
																	borderWidth="1"
																	rounded="md"
																	bgColor="subtle"
																	justifyContent="between"
																>
																	<Box ref={nodeToCopy}>{callbackUrl}</Box>
																	<CopyToClipboardButton
																		size="sm"
																		onClick={async () => {
																			return copyNodeTextToClipboard()
																		}}
																	>
																		<Icons.ClipboardCopy />
																	</CopyToClipboardButton>
																</Inline>
																<Text color="muted" fontSize="sm">
																	Register this exact URL as an authorized
																	redirect URI in your Google OAuth client.
																</Text>
															</Stack>
														)}
													</CopyToClipboard>
												) : null}
											</Box>
											<Box as="li">
												Copy the <mark>Client ID</mark> and{" "}
												<mark>Client secret</mark> from the popup (or{" "}
												<b>Download JSON</b>), then paste or upload them in the
												fields below and save.
											</Box>
										</Stack>
									</Stack>
									<Divider sm />
									<FileInputField
										name="credentials_json_file"
										type="file"
										accept="application/json,.json"
										label="Upload credentials.json"
										secondaryLabel="optional"
										onChange={(files) => {
											const file = files?.[0]
											if (!file) return
											const reader = new FileReader()
											reader.onload = () => {
												const { client_id, client_secret } =
													parseGoogleCredentialsFile(
														String(reader.result || "")
													)
												if (client_id) {
													form.change("client_id", client_id)
												}
												if (client_secret) {
													form.change("client_secret", client_secret)
												}
												if (!client_id && !client_secret) {
													alert(
														"Could not read client_id / client_secret from this file."
													)
												}
											}
											reader.readAsText(file)
										}}
									/>

									<Grid gap="4">
										<Col xs={12} md={6}>
											<TextInputField
												name="client_id"
												label="Client ID"
												required
												type="text"
											/>
										</Col>
										<Col xs={12} md={6}>
											<TextInputField
												name="client_secret"
												label="Client Secret"
												type="password"
												required
												autoComplete="new-password"
												secondaryLabel={configured ? "re-enter to update" : ""}
												help={
													configured
														? "For security the saved secret is never shown. Re-enter it (or upload the file again) to update."
														: undefined
												}
											/>
										</Col>
									</Grid>

									<Divider marginY="0" />
									<SubmissionError />
									<Inline gap="3" justifyContent="between" flexWrap="wrap">
										<Inline gap="3">
											<Button type="submit" disabled={submitting}>
												{submitting ? "Saving..." : "Save Credentials"}
											</Button>
											<Button
												type="button"
												level="tertiary"
												onClick={() => setOpen(false)}
												disabled={submitting}
											>
												Cancel
											</Button>
										</Inline>
										{configured ? (
											<Button
												type="button"
												level="tertiary"
												status="danger"
												onClick={clearCredentials}
												disabled={clearing || submitting}
											>
												<Icons.Trash />{" "}
												{clearing ? "Removing..." : "Remove Custom Client"}
											</Button>
										) : null}
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

type TOutlookOAuthFormValues = {
	client_id: string
	client_secret: string
	tenant_id: string
}

const outlookOAuthFormValidator = Validator.object().shape({
	client_id: Validator.string().required("Application (client) ID is required"),
	client_secret: Validator.string().required("Client secret Value is required"),
	tenant_id: Validator.string(),
})

function OutlookOAuthSettingsDialog({
	app,
	onSaved,
}: {
	app: TMailIntegratedApp
	onSaved: () => Promise<unknown>
}) {
	const xhr = useXHR()
	const [isOpen, setOpen] = useState(false)
	const [clearing, setClearing] = useState(false)
	const configured = Boolean(app.outlook_oauth_configured)
	const callbackUrl = app.outlook_callback_url || ""
	const authUrl = app.outlook_auth_url || ""

	const clearCredentials = async () => {
		if (clearing) return
		if (
			!window.confirm(
				"Remove the custom Outlook OAuth client and revert to the default? Active Outlook mailboxes connected under it must be paused first."
			)
		) {
			return
		}
		setClearing(true)
		try {
			await xhr.delete(`/integrations/mail/${app.id}/outlook-oauth`)
			showSnackbar("Custom Outlook OAuth client removed.")
			await onSaved()
			setOpen(false)
		} catch (e) {
			const error = e as Error
			alert(error.message || "Unable to remove the Outlook OAuth client.")
		} finally {
			setClearing(false)
		}
	}

	return (
		<>
			<Button level="secondary" onClick={() => setOpen(true)}>
				<Icons.Cog /> Outlook OAuth{" "}
				{configured ? (
					<Badge success>Custom</Badge>
				) : authUrl ? (
					<Badge>Default</Badge>
				) : null}
			</Button>
			<Dialog
				open={isOpen}
				onClose={() => setOpen(false)}
				title="Outlook OAuth Client"
				lg
			>
				<Dialog.Body>
					<Form<TOutlookOAuthFormValues>
						initialValues={{
							client_id: app.outlook_oauth_client_id || "",
							client_secret: "",
							tenant_id:
								app.outlook_oauth_tenant_id === "common"
									? ""
									: app.outlook_oauth_tenant_id || "",
						}}
						validate={validateFormValues(outlookOAuthFormValidator)}
						onSubmit={withServerErrors(async (values) => {
							await xhr.put(`/integrations/mail/${app.id}/outlook-oauth`, {
								client_id: values.client_id,
								client_secret: values.client_secret,
								tenant_id: values.tenant_id || null,
							})
							showSnackbar("Outlook OAuth client saved.")
							await onSaved()
							setOpen(false)
						})}
						subscription={{ submitting: true }}
					>
						{({ submitting, handleSubmit }) => (
							<form noValidate onSubmit={handleSubmit} autoComplete="off">
								<Stack gap="4">
									<Alert status="info">
										Connect Outlook / Microsoft 365 mailboxes using your own
										Microsoft Entra app registration. Create the app in the
										Entra admin center, register the redirect URI below, add the
										Exchange Online IMAP/SMTP permissions, then paste the
										Application ID and client secret.
									</Alert>

									<Stack gap="2">
										<Heading as="h4">
											Register an Entra application to get the credentials
										</Heading>
										<Text color="muted" fontSize="sm">
											One-time setup in the Microsoft Entra admin center by an
											admin of your Microsoft 365 tenant.
										</Text>
										<Stack
											as="ol"
											listStyleType="decimal"
											paddingLeft="4"
											gap="2"
										>
											<Box as="li">
												Open the{" "}
												<Text
													as="a"
													href="https://entra.microsoft.com/"
													rel="noopener noreferrer"
													target="_blank"
													color="accent"
												>
													Microsoft Entra admin center
												</Text>{" "}
												and go to{" "}
												<b>Identity → Applications → App registrations</b> →{" "}
												<mark>New registration</mark>.
											</Box>
											<Box as="li">
												Name it (e.g. <b>Sembark Mail</b>). Under supported
												account types prefer{" "}
												<b>Any Entra ID Tenant + Personal Microsoft accounts</b>
												. If you pick single-tenant instead, also fill the{" "}
												<b>Directory (tenant) ID</b> field below.
											</Box>
											<Box as="li">
												<Text>
													Under <b>Redirect URI</b> choose platform <b>Web</b>{" "}
													and paste the URL below, then click{" "}
													<mark>Register</mark>.
												</Text>
												{callbackUrl ? (
													<CopyToClipboard<HTMLDivElement>>
														{({ nodeToCopy, copyNodeTextToClipboard }) => (
															<Stack gap="1">
																<Text fontWeight="semibold">
																	Redirect URI (Web)
																</Text>
																<Inline
																	gap="2"
																	alignItems="center"
																	paddingY="1"
																	paddingLeft="4"
																	paddingRight="1"
																	borderWidth="1"
																	rounded="md"
																	bgColor="subtle"
																	justifyContent="between"
																>
																	<Box ref={nodeToCopy}>{callbackUrl}</Box>
																	<CopyToClipboardButton
																		size="sm"
																		onClick={async () => {
																			return copyNodeTextToClipboard()
																		}}
																	>
																		<Icons.ClipboardCopy />
																	</CopyToClipboardButton>
																</Inline>
															</Stack>
														)}
													</CopyToClipboard>
												) : null}
											</Box>
											<Box as="li">
												Add permissions: <b>API permissions</b> →{" "}
												<mark>Add a permission</mark> → <b>Microsoft Graph</b> →{" "}
												<b>Delegated permissions</b> → search and tick{" "}
												<code>IMAP.AccessAsUser.All</code> and{" "}
												<code>SMTP.Send</code>. (They are listed under Microsoft
												Graph, but grant access to the mailbox over IMAP/SMTP —
												no Graph API is involved.)
											</Box>
											<Box as="li">
												Create a secret: <b>Certificates &amp; secrets</b> →{" "}
												<mark>New client secret</mark>. Copy the secret{" "}
												<b>Value</b> column immediately (not the Secret ID) — it
												is shown only once.
											</Box>
											<Box as="li">
												Paste the <mark>Application (client) ID</mark> and the
												secret <mark>Value</mark> below and save.
											</Box>
										</Stack>
									</Stack>

									<Alert status="warning">
										Entra client secrets expire (24 months max). Set a reminder:
										when the secret expires, connected mailboxes stop refreshing
										until you save a new secret here. Saving a new secret for
										the same Application ID requires no mailbox reconnect.
									</Alert>

									<Grid gap="4">
										<Col xs={12} md={6}>
											<TextInputField
												name="client_id"
												label="Application (client) ID"
												required
												type="text"
											/>
										</Col>
										<Col xs={12} md={6}>
											<TextInputField
												name="client_secret"
												label="Client Secret (Value)"
												type="password"
												required
												autoComplete="new-password"
												secondaryLabel={configured ? "re-enter to update" : ""}
												help={
													configured
														? "For security the saved secret is never shown. Re-enter it to update (e.g. after rotation)."
														: undefined
												}
											/>
										</Col>
										<Col xs={12} md={6}>
											<TextInputField
												name="tenant_id"
												label="Directory (tenant) ID"
												type="text"
												secondaryLabel="optional"
												help="Leave blank unless your app registration is single-tenant."
											/>
										</Col>
									</Grid>

									<Divider marginY="0" />
									<SubmissionError />
									<Inline gap="3" justifyContent="between" flexWrap="wrap">
										<Inline gap="3">
											<Button type="submit" disabled={submitting}>
												{submitting ? "Saving..." : "Save Credentials"}
											</Button>
											<Button
												type="button"
												level="tertiary"
												onClick={() => setOpen(false)}
												disabled={submitting}
											>
												Cancel
											</Button>
										</Inline>
										{configured ? (
											<Button
												type="button"
												level="tertiary"
												status="danger"
												onClick={clearCredentials}
												disabled={clearing || submitting}
											>
												<Icons.Trash />{" "}
												{clearing ? "Removing..." : "Remove Custom Client"}
											</Button>
										) : null}
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function MailboxesTable({
	data,
	paused,
	onUpdated,
	onDelete,
	onTest,
	onSendTestEmail,
	onAccessUpdated,
	onConfigUpdated,
}: {
	data: TMailIntegratedApp
	paused?: boolean
	onUpdated: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onDelete: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onTest: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onSendTestEmail: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onAccessUpdated: (mailbox: TIntegratedMailbox) => Promise<unknown>
	onConfigUpdated: () => Promise<unknown>
}) {
	return (
		<Stack gap="4" padding="4">
			{paused ? (
				<Alert status="warning" title="Email integration paused">
					This email integration is paused. Resume it to add new mailboxes or
					reactivate existing ones.
				</Alert>
			) : null}
			{!paused ? (
				<Inline gap="4" justifyContent="end" flexWrap="wrap">
					{data.gmail_oauth_customizable ? (
						<GmailOAuthSettingsDialog app={data} onSaved={onConfigUpdated} />
					) : null}
					{data.outlook_oauth_customizable ? (
						<OutlookOAuthSettingsDialog app={data} onSaved={onConfigUpdated} />
					) : null}
					{data.gmail_auth_url ? (
						<Button
							level="primary"
							as="a"
							href={data.gmail_auth_url}
							target="_blank"
							rel="noreferrer noopener"
						>
							<Icons.Plus /> Gmail
						</Button>
					) : null}
					{data.outlook_auth_url ? (
						<Button
							level="primary"
							as="a"
							href={data.outlook_auth_url}
							target="_blank"
							rel="noreferrer noopener"
						>
							<Icons.Plus /> Outlook
						</Button>
					) : null}
					<ButtonLink
						to={generatePath(
							"/integrations/:integrationId/email-mailboxes/new",
							{
								integrationId: String(data.integration_uid),
							}
						)}
					>
						<Icons.Plus /> IMAP/SMTP
					</ButtonLink>
				</Inline>
			) : null}
			{!data.mailboxes?.length ? (
				<Box padding={"4"}>
					<Alert status="info">
						No mailboxes have been added yet. Add a mailbox to begin connecting
						and testing your credentials.
					</Alert>
				</Box>
			) : (
				<Table
					responsive
					headers={["Mailbox", "Provider", "Status", "Last Checked", "Actions"]}
					style={{ minHeight: "500px" }}
					alignCols={{ 4: "right" }}
					rows={data.mailboxes.map((mailbox) => [
						<Stack gap="1" key={`${mailbox.id}-mailbox`}>
							<Text fontWeight="semibold">{mailbox.name}</Text>
							<Text color="muted">{mailbox.email}</Text>
							<MailboxAccessList mailbox={mailbox} />
						</Stack>,
						<Badge key={`${mailbox.id}-provider`}>
							{MAILBOX_PROVIDER_LABELS[mailbox.provider] || mailbox.provider}
						</Badge>,
						<Stack gap="1" key={`${mailbox.id}-status`}>
							<Inline gap="2" alignItems="center">
								{!mailbox.is_active ? <Badge warning>Paused</Badge> : null}
								{mailbox.last_connection_status === "success" ? (
									<Badge success>Connected</Badge>
								) : mailbox.last_connection_status === "failed" ? (
									<Badge warning>Failed</Badge>
								) : (
									<Badge>Not Tested</Badge>
								)}
								<Badge
									success={mailbox.inbound_enabled}
									warning={!mailbox.inbound_enabled}
									title={
										mailbox.last_connection_details?.["imap"]
											? (
													mailbox.last_connection_details["imap"] as never as {
														error: "string"
													}
												)["error"]
											: undefined
									}
								>
									{!mailbox.inbound_enabled ? <Icons.Ban size="3" /> : null}{" "}
									IMAP
								</Badge>
								<Badge
									success={mailbox.outbound_enabled}
									warning={!mailbox.outbound_enabled}
									title={
										mailbox.last_connection_details?.["smtp"]
											? (
													mailbox.last_connection_details["smtp"] as never as {
														error: "string"
													}
												)["error"]
											: undefined
									}
								>
									{!mailbox.outbound_enabled ? <Icons.Ban size="3" /> : null}{" "}
									SMTP
								</Badge>
								{mailbox.inbound_enabled ? (
									mailbox.push_read_flag_to_imap ? (
										<Badge title="Marking a mail as read here also marks it read on the IMAP server">
											<Icons.Ok size="3" /> Read Flags
										</Badge>
									) : (
										<Badge
											warning
											title="Read state is tracked locally only; IMAP server is not updated"
										>
											<Icons.EyeSlash /> Local Reads
										</Badge>
									)
								) : null}
								<MailboxSyncStatusBadge mailbox={mailbox} />
							</Inline>
							{mailbox.last_connection_error ? (
								<Text color="danger" fontSize="sm">
									{mailbox.last_connection_error}
								</Text>
							) : null}
							{mailbox.sync_error ? (
								<Text color="danger" fontSize="sm">
									{mailbox.sync_error}
								</Text>
							) : null}
							<MailboxSyncSummary mailbox={mailbox} />
						</Stack>,
						mailbox.last_connection_checked_at ? (
							<Stack gap="1" key={`${mailbox.id}-checked`}>
								<Time timestamp={mailbox.last_connection_checked_at} />
								<Text color="muted" fontSize="sm">
									<RelativeTime
										timestamp={mailbox.last_connection_checked_at}
									/>
								</Text>
							</Stack>
						) : (
							<Text color="muted" key={`${mailbox.id}-checked`}>
								Never
							</Text>
						),
						<MailboxRowActions
							key={`${mailbox.id}-actions`}
							mailbox={mailbox}
							oauthAuthUrls={{
								gmail: data.gmail_auth_url ?? null,
								outlook: data.outlook_auth_url ?? null,
							}}
							onUpdated={onUpdated}
							onDelete={onDelete}
							onTest={onTest}
							onSendTestEmail={onSendTestEmail}
							onAccessUpdated={onAccessUpdated}
						/>,
					])}
				/>
			)}
		</Stack>
	)
}

export function MailboxInboxTab({
	integration,
}: {
	integration: TMailIntegration
}) {
	const xhr = useXHR()
	const {
		data,
		error: mailboxesError,
		mutate: mutateMailboxes,
	} = useFetchMailIntegratedApp(integration.integrated_app_id, {
		refreshInterval(currentData) {
			return shouldPollMailboxIntegration(currentData)
				? MAILBOX_SYNC_POLL_INTERVAL
				: 0
		},
	})
	const inboundMailboxes = (data?.mailboxes || []).filter(
		(mailbox) => mailbox.inbound_enabled
	)
	const [selectedMailboxId, setSelectedMailboxId] = useState<number | null>(
		inboundMailboxes[0]?.id || null
	)

	useEffect(() => {
		if (!selectedMailboxId && inboundMailboxes[0]?.id) {
			setSelectedMailboxId(inboundMailboxes[0].id)
		}
		if (
			selectedMailboxId &&
			!inboundMailboxes.some((mailbox) => mailbox.id === selectedMailboxId)
		) {
			setSelectedMailboxId(inboundMailboxes[0]?.id || null)
		}
	}, [inboundMailboxes, selectedMailboxId])

	const selectedMailbox =
		inboundMailboxes.find((mailbox) => mailbox.id === selectedMailboxId) || null

	const [params, setParams] = useSearch<TSearchParams & { mailbox?: number }>()
	const [forceRefreshKey, setForceRefreshKey] = useState(0)

	useEffect(() => {
		if (params.mailbox && Number(params.mailbox) !== selectedMailboxId) {
			setSelectedMailboxId(Number(params.mailbox))
		}
	}, [params.mailbox, selectedMailboxId])

	if (!data) {
		if (!mailboxesError) {
			return <Spinner alignCenter padding="8" />
		}
		return (
			<Alert status="error">
				{mailboxesError.message || "Unable to load mailboxes"}
			</Alert>
		)
	}

	if (!inboundMailboxes.length) {
		return (
			<Box padding="4">
				<Alert status="info">
					No incoming mailbox is available yet. Enable incoming sync on a
					mailbox first to view synced conversations here.
				</Alert>
			</Box>
		)
	}

	return (
		<Search
			title={
				<Stack gap="1">
					<Box fontSize="lg">Inbox</Box>
					{selectedMailbox ? (
						<Stack fontSize="base" fontWeight="normal">
							<Inline gap="4">
								<Inline gap="2" alignItems="center" flexWrap="wrap">
									<MailboxSyncStatusBadge mailbox={selectedMailbox} />
									{selectedMailbox.sync_status === "queued" &&
									selectedMailbox.last_sync_queued_at ? (
										<Text color="muted" fontSize="sm">
											Queued{" "}
											<RelativeTime
												timestamp={selectedMailbox.last_sync_queued_at}
											/>
										</Text>
									) : null}
									{selectedMailbox.sync_status === "syncing" &&
									selectedMailbox.last_sync_started_at ? (
										<Text as="span" color="muted" fontSize="sm">
											Started{" "}
											<RelativeTime
												timestamp={selectedMailbox.last_sync_started_at}
											/>
										</Text>
									) : null}
								</Inline>
								{selectedMailbox.last_synced_at ? (
									<Inline gap="2" alignItems="center" flexWrap="wrap">
										<Text as="span">Last Sync:</Text>
										<Time
											timestamp={selectedMailbox.last_synced_at}
											dateFormat
											timeFormat
										/>
									</Inline>
								) : null}
							</Inline>
							{selectedMailbox.sync_error ? (
								<Alert status="error">{selectedMailbox.sync_error}</Alert>
							) : null}
						</Stack>
					) : (
						<Alert status="info">
							This mailbox has not been synced yet. Run the inbox sync to pull
							the latest IMAP messages.
						</Alert>
					)}
				</Stack>
			}
			onSearch={(params) => setParams({ ...params, page: 1 })}
			placeholder="Search by Thread/Subject..."
			actions={
				<Inline gap="2">
					<Box
						as="select"
						id="mailbox_inbox_selector"
						value={String(selectedMailboxId || "")}
						onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
							setSelectedMailboxId(Number(e.target.value))
						}
						paddingY="2"
						paddingX="3"
						rounded="md"
						borderWidth="1"
						bgColor="white"
					>
						{inboundMailboxes.map((mailbox) => (
							<option key={mailbox.id} value={mailbox.id}>
								{mailbox.name} ({mailbox.email})
							</option>
						))}
					</Box>
					<Component initialState={false}>
						{({ state: isDialogOpen, setState: setDialogOpenState }) => (
							<>
								<Dropdown alignRight>
									<Dropdown.ToggleButton>
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										<Dropdown.MenuItem
											onClick={(e) => {
												e.preventDefault()
												if (
													selectedMailbox?.sync_status === "queued" ||
													selectedMailbox?.sync_status === "syncing"
												) {
													alert(
														"Synchronisation is in-process. Please retry after sometime."
													)
													return
												}
												setDialogOpenState(true)
											}}
										>
											<Icons.Refresh /> Sync Now
										</Dropdown.MenuItem>
									</Dropdown.Menu>
								</Dropdown>
								<Dialog
									open={isDialogOpen}
									onClose={() => setDialogOpenState(false)}
									title="Synchronise Mailbox"
									sm
								>
									<Dialog.Body>
										<Form<unknown>
											onSubmit={withServerErrors(async () => {
												if (!selectedMailboxId) {
													return
												}
												await xhr.post(`/mailboxes/${selectedMailboxId}/sync`)
												showSnackbar("Mailbox inbox sync queued.")
												setForceRefreshKey((key) => key + 1)
												await mutateMailboxes()
												setDialogOpenState(false)
											})}
											subscription={{ submitting: true }}
										>
											{({ handleSubmit, submitting }) => (
												<form noValidate onSubmit={handleSubmit}>
													<Stack gap="4">
														<Alert status="info">
															System automatically synchornises your mailboxes
															periodically.
														</Alert>
														<Divider marginY="0" />
														<SubmissionError />
														<Inline gap="4">
															<Button
																type="submit"
																disabled={submitting || !selectedMailboxId}
															>
																<Icons.Refresh spin={submitting} />{" "}
																{submitting ? "Syncing..." : "Sync Now"}
															</Button>
															<Button onClick={() => setDialogOpenState(false)}>
																Cancel
															</Button>
														</Inline>
													</Stack>
												</form>
											)}
										</Form>
									</Dialog.Body>
								</Dialog>
							</>
						)}
					</Component>
				</Inline>
			}
		>
			{() =>
				!selectedMailboxId ? null : (
					<ListView<TMailThread>
						pageKey={`mailbox-inbox/${selectedMailboxId}?${forceRefreshKey}`}
						params={params}
						fetch={(xhr, params) =>
							xhr
								.get(`/mail-threads`, {
									params: {
										...params,
										mailbox_id: selectedMailboxId,
									},
								})
								.then((resp) => resp.data)
						}
						onPageChange={(page) => setParams({ ...params, page })}
						swrConfig={{ refreshInterval: 60000, revalidateOnFocus: true }}
						emptyState={({ searched, defaultValue, onRefresh }) =>
							searched ? (
								defaultValue
							) : (
								<Container paddingY="4">
									<Stack gap="4">
										<Text>No synced messages found for this mailbox yet.</Text>
										<Box>
											<Inline gap="4">
												<Button onClick={() => onRefresh()}>Refresh</Button>
											</Inline>
										</Box>
									</Stack>
								</Container>
							)
						}
					>
						{({ items }) => (
							<Stack
								gap="0"
								borderWidth="1"
								rounded="md"
								bgColor="default"
								overflow="hidden"
							>
								{items.map((thread, index) => (
									<MailThreadListRow
										key={thread.id}
										thread={thread}
										integrationUid={integration.uid}
										selectedMailboxId={selectedMailboxId}
										isLast={index === items.length - 1}
									/>
								))}
							</Stack>
						)}
					</ListView>
				)
			}
		</Search>
	)
}

function ThreadSenderAvatar({
	name,
	email,
}: {
	name?: string | null
	email?: string | null
}) {
	const source = (name || email || "?").trim()
	const tokens = source.split(/[\s_.@-]+/).filter(Boolean)
	const initials = tokens.length
		? tokens
				.map((t) => t.match(/[A-Za-z0-9]/)?.[0] || "")
				.filter(Boolean)
				.slice(0, 2)
				.join("")
				.toUpperCase()
		: "?"
	return (
		<Box
			width="8"
			height="8"
			rounded="full"
			bgColor="primary"
			display="flex"
			alignItems="center"
			justifyContent="center"
			flexShrink="0"
			borderWidth="1"
			borderColor="primary"
		>
			<Text fontWeight="semibold" fontSize="xs" color="primary">
				{initials.slice(0, 2) || "?"}
			</Text>
		</Box>
	)
}

function MailThreadListRow({
	thread,
	integrationUid,
	selectedMailboxId,
	isLast,
}: {
	thread: TMailThread
	integrationUid: string
	selectedMailboxId: number | null
	isLast: boolean
}) {
	const detailHref =
		generatePath(
			"/integrations/:integrationId/mail/:folder/threads/:threadId",
			{
				integrationId: integrationUid,
				folder: "inbox",
				threadId: String(thread.id),
			}
		) + queryToSearch({ mailbox: selectedMailboxId })

	const latest = thread.latest_message
	const parsing = latest?.latest_parsing
	const isUnread = latest?.is_read === false
	const senderName = latest?.from_name || latest?.from_email || "Unknown Sender"
	const subject = thread.subject || latest?.subject || "(No Subject)"
	const preview = getMailMessagePlainText({
		textBody: latest?.text_body,
	})
		.replace(/\s+/g, " ")
		.trim()
		.slice(0, 200)
	const timestamp =
		thread.latest_message_at || latest?.received_at || latest?.sent_at

	return (
		<Box
			borderBottomWidth={isLast ? "0" : "1"}
			bgColor={isUnread ? "default" : "inset"}
			position="relative"
		>
			<Link to={detailHref} display="block" padding="3" color="default">
				<Inline gap="3" alignItems="start">
					<ThreadSenderAvatar
						name={latest?.from_name}
						email={latest?.from_email}
					/>
					<Stack gap="1" flex="1" minWidth="0">
						<Inline
							justifyContent="between"
							alignItems="baseline"
							gap="3"
							flexWrap="wrap"
						>
							<Inline
								gap="2"
								alignItems="baseline"
								flexWrap="wrap"
								minWidth="0"
							>
								<Text
									fontSize="sm"
									fontWeight={isUnread ? "semibold" : "normal"}
								>
									{senderName}
								</Text>
								<Text fontSize="sm" color="muted">
									|
								</Text>
								<Text
									fontSize="sm"
									fontWeight={isUnread ? "semibold" : "normal"}
								>
									{subject}
								</Text>
							</Inline>
							<Inline gap="2" alignItems="center" flexWrap="wrap">
								{parsing?.classification_label ? (
									<Badge
										fullRounded
										outlined
										title={parsing.error || undefined}
									>
										<Icons.SparklesSolid size="3" />{" "}
										{parsing.classification_label}
									</Badge>
								) : parsing?.status === "skipped" ? (
									<Badge fullRounded outlined warning>
										Parsing skipped
									</Badge>
								) : parsing?.status === "failed" ? (
									<Badge fullRounded outlined status="danger">
										Parsing failed
									</Badge>
								) : parsing?.status === "pending" ||
								  parsing?.status === "submitted" ? (
									<Badge fullRounded outlined>
										Parsing…
									</Badge>
								) : null}
								{thread.links?.map((link) => (
									<Badge
										key={`${link.type}-${link.id}`}
										fullRounded
										status="accent"
									>
										{link.name}
									</Badge>
								))}
								{timestamp ? (
									<Text color="muted" fontSize="sm">
										<RelativeTime timestamp={timestamp} />
									</Text>
								) : null}
							</Inline>
						</Inline>
						{preview ? (
							<Text fontSize="sm" color="muted">
								{preview}
							</Text>
						) : null}
						<Text color="muted" fontSize="xs">
							{thread.messages_count || 0} message
							{(thread.messages_count || 0) > 1 ? "s" : ""}
						</Text>
					</Stack>
				</Inline>
			</Link>
		</Box>
	)
}

export function MailThreadDetails({
	integration,
	threadId,
	mailboxId,
}: {
	integration: TMailIntegration
	threadId: string | number
	mailboxId?: string | number | null
}) {
	const xhr = useXHR()
	const { data: integrationApp, error: mailboxesError } =
		useFetchMailIntegratedApp(integration.integrated_app_id)
	const inboundMailboxes = (integrationApp?.mailboxes || []).filter(
		(mailbox) => mailbox.inbound_enabled
	)
	const selectedMailboxId = Number(mailboxId) || inboundMailboxes[0]?.id || null
	const {
		data: threadResponse,
		error: threadError,
		isLoading,
		mutate,
	} = useSWR<{ data: TMailThread }>(
		selectedMailboxId ? `/mail-threads/${threadId}` : null,
		(url: string) => xhr.get(url).then((resp) => resp.data)
	)
	if (!integrationApp) {
		if (!mailboxesError) {
			return <Spinner alignCenter padding="8" />
		}

		return (
			<Alert status="error">
				{mailboxesError.message || "Unable to load mailbox details"}
			</Alert>
		)
	}

	if (!selectedMailboxId) {
		return (
			<Alert status="info">
				No incoming mailbox is available yet. Enable incoming sync on a mailbox
				first to view synced conversations here.
			</Alert>
		)
	}

	if (isLoading) {
		return <Spinner alignCenter padding="8" />
	}

	if (threadError) {
		return (
			<Alert status="error">
				{threadError.message || "Unable to load synced thread"}
			</Alert>
		)
	}

	const thread = threadResponse?.data
	const selectedMailbox =
		integrationApp.mailboxes?.find(
			(mailbox) => mailbox.id === selectedMailboxId
		) || null

	return (
		<Stack gap="4" padding="4">
			<Inline gap="3" alignItems="center" flexWrap="wrap">
				<ButtonLink
					to={
						generatePath("/integrations/:integrationId/mail/:folder", {
							integrationId: integration.uid,
							folder: "inbox",
						}) + queryToSearch({ mailbox: selectedMailboxId })
					}
					level="tertiary"
					size="sm"
				>
					<Icons.ArrowLeft /> Back to Inbox
				</ButtonLink>
				<Badge>{thread?.messages_count || 0} messages</Badge>
			</Inline>
			{!thread ? (
				<Alert status="info">Thread not found.</Alert>
			) : (
				<>
					<Stack gap="1">
						<Heading as="h4">{thread.subject || "(No Subject)"}</Heading>
						<Text color="muted">
							Read-only synced conversation from the connected mailbox.
						</Text>
					</Stack>
					<Stack gap="2">
						<Heading as="h5" fontSize="base">
							Linked Records
						</Heading>
						{thread.links?.length ? (
							<Stack gap="2">
								{thread.links.map((link) => (
									<Inline
										key={`${link.type}-${link.id}`}
										gap="2"
										alignItems="center"
										flexWrap="wrap"
									>
										<Badge>{link.name}</Badge>
									</Inline>
								))}
							</Stack>
						) : (
							<Text color="muted">
								This thread is not linked to any lead, trip, or booking yet.
							</Text>
						)}
					</Stack>
					<Stack gap="4">
						{(thread.messages || []).map((message) => (
							<Box key={message.id} borderWidth="1" rounded="md" padding="4">
								<Stack gap="3">
									<Inline
										justifyContent="between"
										alignItems="start"
										flexWrap="wrap"
										gap="3"
									>
										<Stack gap="1">
											<Text fontWeight="semibold">
												{message.from_name ||
													message.from_email ||
													"Unknown Sender"}
											</Text>
											{message.from_email ? (
												<Text color="muted" fontSize="sm">
													{message.from_email}
												</Text>
											) : null}
											{message.direction === "outbound" &&
											message.created_by?.name ? (
												<Text color="muted" fontSize="sm">
													Sent by {message.created_by.name}
												</Text>
											) : null}
										</Stack>
										{message.received_at || message.sent_at ? (
											<Time
												timestamp={
													message.received_at || message.sent_at || undefined
												}
												dateFormat
												timeFormat
											/>
										) : null}
									</Inline>
									{message.participants?.length ? (
										<Inline gap="2" flexWrap="wrap">
											{message.participants.map((participant) => (
												<Badge key={participant.id}>
													{participant.type}:{" "}
													{participant.name
														? `${participant.name} <${participant.email}>`
														: participant.email}
												</Badge>
											))}
										</Inline>
									) : null}
									<MailMessageBody
										htmlBody={message.latest_html_body || message.html_body}
										textBody={message.latest_text_body || message.text_body}
										quotedHtmlBody={message.quoted_html_body}
										quotedTextBody={message.quoted_text_body}
										messageLabel="thread message"
										bodyAvailable={message.body_available}
										preview={message.preview}
									/>
								</Stack>
							</Box>
						))}
					</Stack>
					{selectedMailbox?.outbound_enabled ? (
						<Box borderWidth="1" rounded="md" padding="4">
							<Stack gap="3">
								<Stack gap="1">
									<Heading as="h5" fontSize="base">
										Reply
									</Heading>
									<Text color="muted" fontSize="sm">
										Send a reply from{" "}
										{selectedMailbox.name || selectedMailbox.email}.
									</Text>
								</Stack>
								<Form<{ body: string; include_signature: boolean | 1 | 0 }>
									initialValues={{ body: "", include_signature: 1 }}
									onSubmit={withServerErrors(async (values) => {
										await xhr.post(`/mail-threads/${threadId}/reply`, {
											body: values.body,
											include_signature: isTruthy(values.include_signature)
												? 1
												: 0,
										})
										await mutate()
										showSnackbar("Reply sent successfully")
									})}
									subscription={{ submitting: true }}
								>
									{({ submitting, handleSubmit, form }) => (
										<form noValidate onSubmit={handleSubmit}>
											<Stack gap="3">
												<MarkdownInputField
													name="body"
													label="Message"
													placeholder="Write your reply in markdown"
												/>
												<SwitchInputField
													name="include_signature"
													label="Include my email signature"
													disabled={submitting}
												/>
												<SubmissionError />
												<Inline gap="3">
													<Button type="submit" disabled={submitting}>
														{submitting ? "Sending..." : "Send Reply"}
													</Button>
													<Button
														type="button"
														level="secondary"
														onClick={() => form.reset()}
														disabled={submitting}
													>
														Clear
													</Button>
												</Inline>
											</Stack>
										</form>
									)}
								</Form>
							</Stack>
						</Box>
					) : (
						<Alert status="info">
							Outgoing send is not enabled for this mailbox, so replies can not
							be sent from this thread yet.
						</Alert>
					)}
				</>
			)}
		</Stack>
	)
}

function useScrollableContainer() {
	const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
	const [progress, setProgress] = useState(0)
	const [ref, setRef] = useCallbackRef<HTMLElement>()

	const setContainerRef: typeof setRef = useCallback(
		(node) => {
			setHasScrolledToEnd(false)
			setRef(node)
		},
		[setRef, setHasScrolledToEnd]
	)

	useEffect(() => {
		const el = ref
		if (!el) return
		function handleScroll() {
			if (!el) return
			const { scrollTop, scrollHeight, clientHeight } = el
			const scrolled = scrollTop + clientHeight
			const progressPercent = Math.min((scrolled / scrollHeight) * 100, 100)
			setProgress(progressPercent)
			setHasScrolledToEnd((prev) => prev || scrolled >= scrollHeight - 5)
		}
		el.addEventListener("scroll", handleScroll)
		handleScroll()
		return () => {
			el.removeEventListener("scroll", handleScroll)
		}
	}, [ref])

	return { hasScrolledToEnd, progress, ref: setContainerRef }
}

const MAILBOX_TNC = `### 1. Scope of Services

Sembark Tech Private Limited acts solely as a technology provider, offering an integration that connects your mailbox (IMAP/SMTP, OAuth, or other supported protocols) to the Sembark CRM platform. The integration is intended to streamline conversation management, lead capture, and reply workflows tied to your CRM data.

### 2. Credentials and Authorisation

You confirm that the mailbox credentials, tokens, or app passwords you provide are yours to share with Sembark, and that you have the necessary internal authorisation to grant Sembark access for the configured tasks (read, sync, send, mark-as-read on the upstream server).

### 3. Data Handling

Mailbox credentials are stored encrypted at rest. Synced messages and metadata are stored against your tenant account, scoped to the configured mailbox, and used only to power Sembark CRM features such as conversation threading, parsing, attaching to leads/trips, and outbound replies.

### 4. Upstream Provider Responsibility

Mailbox availability, deliverability, quota, blocklists, account suspensions, OAuth scope revocations, and any other provider-side conditions remain entirely your responsibility with the upstream mail provider (Google Workspace, Microsoft 365, Zoho, etc.). Sembark does not guarantee provider-side delivery and is not liable for provider-imposed limits or actions.

### 5. AI Parsing (Optional)

When enabled, Sembark may forward parts of incoming mail content to its AI parser to extract leads and supporting context. The credit usage for such parsing is metered separately and shown in the mailbox parsing usage view.

### 6. Termination

You may disconnect or delete a mailbox integration at any time. Once disconnected, Sembark will stop syncing new messages and sending outbound mail on your behalf. Historical synced data, audit logs, and credit usage records are retained for your reference and billing reconciliation.

### 7. Updates

We may update or amend these Terms & Conditions periodically to reflect changes in our services or applicable laws. Continued use of the mailbox integration after such updates constitutes acceptance of the revised terms.`
